'use client';

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query';
import {
  buildTimelineCopySourceContext,
  extractPathAfterBulk,
  getCaseDisplayName,
  handleDownload,
  toProvenanceLookupPath,
} from '@/shared/functions';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import { useCasesList } from '@/hooks/useCasesList';
import { resolveCaseFolderName, resolveCaseScope } from '@/shared/case-summary';
import { useProvenanceFiles } from '@/hooks/useProvenanceFiles';
import type { UfdrPreviewContext } from '@/hooks/useUfdrExportData';
import { useWindowSize } from '@/shared/clientFunctions';
import { AuthContext, FeaturesContext, FullScreenContext, ToastContext, useReactiveFeatureFlagValue } from '@/app/contexts';
import { ToastLevel } from '@/components/ui/toast';
import { AD_HOC_CASE_DISPLAY_NAME } from '@/shared/ad-hoc-case';
import { usePersonalFilesCaseId } from '@/hooks/usePersonalFilesCaseId';
import { EdgeLambdaApiError, purgeManualCaseFiles } from '@/shared/cors-edge-lambda-api';
import {
  AD_HOC_MANUAL_FOLDER_NAME,
  isAdHocCase,
  isAdHocManualTreeFile,
  matchesAdHocManualFileName,
} from '@/shared/ad-hoc-evidence';
import {
  provenanceKeys,
  type ProvenanceFilesPage,
  type ProvenanceTreePage,
} from '@/shared/cors-provenance-api';
import { META_FILE_REGEX } from '@/shared/constants';
import type { MediaSource, ScopesObject, Source } from '@/shared/types';
import type { FileSummary, ProvenanceFilesFilters, TreeNodeResponse } from '@/shared/cors-provenance-api';
import type { EvidenceLockerTreeHandle } from './evidence-locker-tree-new';
import { useMediaSourceShare } from './mediaSourceShare';
import type { EventFromChat } from './answer-item';
import {
  type EvidenceObject,
  fileTypesTokensForEvidenceListing,
} from './evidence-locker-ui-shared-new';
import { useChatEvidenceLockerNew } from './chat-evidence-locker-context-new';
import { useEvidenceFileTypeFiltersNew } from './use-evidence-file-type-filters-new';

function filterTreePageItems(
  page: ProvenanceTreePage,
  fileName: string,
  caseId: string | undefined | null,
): ProvenanceTreePage {
  const removed = page.items.filter(
    (node) =>
      node.nodeType === 'file' &&
      node.name.trim() === fileName &&
      isAdHocManualTreeFile(node, caseId),
  ).length;
  if (removed === 0) return page;
  return {
    ...page,
    items: page.items.filter(
      (node) =>
        !(
          node.nodeType === 'file' &&
          node.name.trim() === fileName &&
          isAdHocManualTreeFile(node, caseId)
        ),
    ),
    total: Math.max(0, page.total - removed),
  };
}

function filterFilesPageItems(
  page: ProvenanceFilesPage,
  fileName: string,
  caseId: string | undefined | null,
): ProvenanceFilesPage {
  const removed = page.items.filter((f) =>
    matchesAdHocManualFileName(f.searchablePath ?? '', fileName, caseId),
  ).length;
  if (removed === 0) return page;
  return {
    ...page,
    items: page.items.filter(
      (f) => !matchesAdHocManualFileName(f.searchablePath ?? '', fileName, caseId),
    ),
    total: Math.max(0, page.total - removed),
  };
}

/** Drop a deleted Manual file from every cached provenance list immediately. */
function optimisticallyRemoveAdHocManualFile(
  queryClient: QueryClient,
  fileName: string,
  caseId: string | undefined | null,
): void {
  const trimmed = fileName.trim();
  if (!trimmed) return;

  queryClient.setQueriesData<InfiniteData<ProvenanceTreePage>>(
    { queryKey: provenanceKeys.tree() },
    (old) => {
      if (!old?.pages?.length) return old;
      return {
        ...old,
        pages: old.pages.map((page) => filterTreePageItems(page, trimmed, caseId)),
      };
    },
  );

  queryClient.setQueriesData<TreeNodeResponse[]>(
    { queryKey: provenanceKeys.treeSearch() },
    (old) => {
      if (!old?.length) return old;
      return old.filter(
        (node) =>
          !(
            node.nodeType === 'file' &&
            node.name.trim() === trimmed &&
            isAdHocManualTreeFile(node, caseId)
          ),
      );
    },
  );

  queryClient.setQueriesData<InfiniteData<ProvenanceFilesPage>>(
    { queryKey: provenanceKeys.files() },
    (old) => {
      if (!old?.pages?.length) return old;
      return {
        ...old,
        pages: old.pages.map((page) => filterFilesPageItems(page, trimmed, caseId)),
      };
    },
  );
}

export type UseEvidenceLockerPanelNewOptions = {
  isActive?: boolean;
  /** Omit on surfaces with no timeline (HelioTrace); the create-event actions are then hidden. */
  addToTimeline?: (event: EventFromChat) => void;
};

export function useEvidenceLockerPanelNew({
  isActive = true,
  addToTimeline,
}: UseEvidenceLockerPanelNewOptions) {
  const {
    caseId,
    scopesAndFiles,
    setScopesAndFiles,
    addSourceToScope,
  } = useChatEvidenceLockerNew();
  const {
    contentCategoriesEnabled,
    showFolders,
    allFileTypes,
    activeFileTypes,
    selectedContentCategories,
    toggleFileType,
    toggleShowFolders,
    toggleAllFileTypes,
    activeFileTypeChipCount,
    evidenceLockerCategoryScopeActive,
  } = useEvidenceFileTypeFiltersNew();

  const { width } = useWindowSize();
  const isSmallScreen = width !== undefined && width < 450;
  const featuresContext = useContext(FeaturesContext);
  const featureFlags = featuresContext?.featureFlags || {};
  const { accessToken } = useContext(AuthContext) || {};
  const { isFullscreen, setIsFullscreen, setFullscreenMediaItem, linkedMediaItem } =
    useContext(FullScreenContext) || {};
  const { setToast, setToastOpen } = useContext(ToastContext);
  const queryClient = useQueryClient();
  const mediaSourceShare = useMediaSourceShare();
  const { cellId: downloadCellId, accessToken: downloadAccessToken } =
    useCellServiceSession();

  const [loadingEvidence, setLoadingEvidence] = useState(false);
  const [availableEvidence, setAvailableEvidence] = useState<EvidenceObject[]>([]);
  const [selectedEvidence, setSelectedEvidence] = useState<string[]>([]);
  const [excludedEvidence, setExcludedEvidence] = useState<string[]>([]);
  const [filesInputValue, setEvidenceInputValue] = useState('');
  const [loadingScopes, setLoadingScopes] = useState(false);
  const [availableScopes, setAvailableScopes] = useState<string[]>([]);
  const [allScopes, setAllScopes] = useState<string[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [excludedScopes, setExcludedScopes] = useState<string[]>([]);
  const [scopeInputValue, setScopeInputValue] = useState('');
  const [copiedSource, setCopiedSource] = useState(-1);
  const [addedSource, setAddedSource] = useState(-1);
  const [previewPopoverOpen, setPreviewPopoverOpen] = useState(-1);
  const [previewPopoverUrl, setPreviewPopoverUrl] = useState<string | null>(null);
  const [previewPageCount, setPreviewPageCount] = useState<number | undefined>();
  const [hoveredIconKey, setHoveredIconKey] = useState<string | null>(null);
  // Pending content-moderation acknowledgement. ``index`` points at the matching
  // ``availableEvidence`` entry when the file is present in the flat list, or is
  // ``-1`` for files reached purely via the tree (e.g. deeply nested files that
  // never appeared in the first page of ``/files``) — those are reopened by URL.
  const [contentModerationAck, setContentModerationAck] = useState<{
    type: string;
    index: number;
    url: string;
    pageCount: number | null;
    previewUrl: string | null;
    fileInfo: FileSummary | null;
    /** Set when the acknowledged file is a UFDR/zip root. The forensic viewer opens after the ack. */
    ufdr?: {
      url: string;
      fileStatusId?: string | null;
      label?: string;
      fileInfo?: FileSummary | null;
      path?: string | null;
      caseFolderName?: string | null;
    } | null;
  } | null>(null);
  // Content-moderation type + obfuscated preview URL for a URL-based preview
  // (tree row not in the flat list). Carries the flag and the CM-safe thumbnail
  // through so the overlay still obfuscates by default.
  const [previewPopoverCmType, setPreviewPopoverCmType] = useState<string | null>(null);
  const [previewPopoverPreviewUrl, setPreviewPopoverPreviewUrl] = useState<string | null>(null);
  const [previewPopoverFileInfo, setPreviewPopoverFileInfo] = useState<FileSummary | null>(null);
  const [showObfuscatedPreview, setShowObfuscatedPreview] = useState(true);
  const [ufdrPreviewContext, setUfdrPreviewContext] = useState<UfdrPreviewContext | null>(null);
  const [isExcludeAllActive, setIsExcludeAllActive] = useState(false);
  const [isIncludeAllActive, setIsIncludeAllActive] = useState(false);
  const [purgingManualFileName, setPurgingManualFileName] = useState<string | null>(null);

  const adHocEnabled = useReactiveFeatureFlagValue('adHoc') === true;
  const personalFilesCaseId = usePersonalFilesCaseId();
  const isAdHocEvidenceCase = adHocEnabled && isAdHocCase(caseId, personalFilesCaseId);
  const adHocAutoBrowseSubfolder = isAdHocEvidenceCase ? AD_HOC_MANUAL_FOLDER_NAME : null;

  const [filesSearchTerm] = useDebounce(filesInputValue, 500);
  const [scopeSearchTerm] = useDebounce(scopeInputValue, 500);

  const evidenceTreeRef = useRef<EvidenceLockerTreeHandle>(null);
  const previewPopoverRef = useRef<HTMLDivElement>(null);
  const filterBarRowRef = useRef<HTMLDivElement>(null);
  const casesQuery = useCasesList();

  const handleDownloadWithSession = useCallback(
    (sourceObject: MediaSource | Source) => {
      const session =
        downloadCellId && downloadAccessToken
          ? { cellId: downloadCellId, accessToken: downloadAccessToken }
          : undefined;
      handleDownload(sourceObject, session);
    },
    [downloadCellId, downloadAccessToken],
  );

  const shouldLoadEvidenceFiles = useMemo(() => {
    const activeCategoryKeys = Object.keys(activeFileTypes).filter(
      (key) => activeFileTypes[key] === true,
    );
    return !showFolders || allFileTypes || activeCategoryKeys.length > 0;
  }, [showFolders, allFileTypes, activeFileTypes]);

  const caseScope = useMemo(
    () => resolveCaseScope(casesQuery.data, caseId),
    [caseId, casesQuery.data],
  );
  const caseTag = caseScope.tag;
  /**
   * A real case we cannot tag. Every provenance query stays off while this is
   * true: omitting the ``tags`` filter would list the whole tenant under one
   * migrant's Photos & Documents.
   */
  const caseScopeUnresolved = caseScope.kind === 'unresolved';
  /**
   * Split the unresolved state by whether the cases list can still answer.
   * ``pending`` is a spinner; ``missing`` is a terminal empty state (a case too
   * new to be indexed yet). Treating ``missing`` as pending spins forever.
   */
  const casesAnswered = casesQuery.data !== undefined || casesQuery.isError;
  const caseScopePending = caseScopeUnresolved && !casesAnswered;
  const caseScopeMissing = caseScopeUnresolved && casesAnswered;

  const evidenceFilesFilters: ProvenanceFilesFilters = useMemo(() => {
    const trimmed = filesSearchTerm.trim();
    const unquoted =
      trimmed.startsWith('"') && trimmed.endsWith('"')
        ? trimmed.slice(1, -1)
        : trimmed;
    const chipKeys = allFileTypes
      ? []
      : Object.keys(activeFileTypes).filter((k) => activeFileTypes[k] === true);
    const contentCategories =
      chipKeys.length > 0 ? fileTypesTokensForEvidenceListing(chipKeys) : undefined;
    return {
      ...(unquoted.length > 2 ? { searchQuery: unquoted } : {}),
      ...(contentCategories ? { contentCategories } : {}),
      ...(caseTag ? { tags: [caseTag], tagMatch: 'any' as const } : {}),
    };
  }, [filesSearchTerm, allFileTypes, activeFileTypes, caseTag]);

  const scopeLabelByTag = useMemo(() => {
    if (!casesQuery.data?.length) return {} as Record<string, string>;
    const m: Record<string, string> = {};
    for (const c of casesQuery.data) {
      const tag = c.caseTag?.trim();
      if (!tag) continue;
      m[tag] = getCaseDisplayName(c.caseName?.trim() ? c.caseName.trim() : tag);
    }
    return m;
  }, [casesQuery.data]);

  const filesQuery = useProvenanceFiles(evidenceFilesFilters, {
    enabled: shouldLoadEvidenceFiles && isActive && !caseScopeUnresolved,
    pageSize: 100,
  });

  useEffect(() => {
    setSelectedEvidence([]);
    setSelectedScopes([]);
  }, [caseId, filesSearchTerm]);

  useEffect(() => {
    if (!shouldLoadEvidenceFiles) {
      setLoadingEvidence(false);
      return;
    }
    // The query is off until the case resolves. Only spin while the cases list
    // can still answer — once it has, this case has no sources to show.
    if (caseScopeUnresolved) {
      setLoadingEvidence(caseScopePending);
      return;
    }
    setLoadingEvidence(filesQuery.isLoading || filesQuery.isFetching);
  }, [
    shouldLoadEvidenceFiles,
    caseScopeUnresolved,
    caseScopePending,
    filesQuery.isLoading,
    filesQuery.isFetching,
  ]);

  useEffect(() => {
    if (!shouldLoadEvidenceFiles || caseScopeUnresolved) {
      setAvailableEvidence([]);
      return;
    }
    if (!filesQuery.data) return;
    const items = filesQuery.data.pages.flatMap((p) => p.items) as FileSummary[];
    const mapped: EvidenceObject[] = items.map((f) => ({
      url: toProvenanceLookupPath(f.searchablePath),
      mediaType: f.extension ?? '',
      pageCount: f.pageCount ?? null,
      previewUrl: f.previewUrl ?? null,
      contentModerationType:
        f.contentModeration?.type && f.contentModeration.findingStatus !== 'REVIEWED'
          ? f.contentModeration.type
          : null,
      fileInfo: f,
    }));
    setAvailableEvidence(mapped);
  }, [filesQuery.data, shouldLoadEvidenceFiles, caseScopeUnresolved]);

  useEffect(() => {
    if (!accessToken || !caseId || caseId.trim() === '') {
      setAllScopes([]);
      setAvailableScopes([]);
      setLoadingScopes(false);
      return;
    }
    if (caseId !== '*') {
      setAllScopes([]);
      setAvailableScopes([]);
      setLoadingScopes(false);
      return;
    }
    const listLoading =
      casesQuery.isLoading ||
      (casesQuery.isPending && casesQuery.data === undefined && !casesQuery.isError);
    setLoadingScopes(listLoading);
    const list = casesQuery.data;
    if (!list?.length) {
      setAllScopes([]);
      setAvailableScopes([]);
      return;
    }
    const rows = list
      .filter((c) => c.caseTag.trim() !== '' && c.caseTag !== caseId)
      .map((c) => ({
        tag: c.caseTag,
        label: getCaseDisplayName(c.caseName?.trim() ? c.caseName.trim() : c.caseTag),
      }));
    rows.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
    const sortedTags = rows.map((r) => r.tag);
    setAllScopes(sortedTags);
    setAvailableScopes(sortedTags.slice(0, 250));
  }, [
    caseId,
    accessToken,
    casesQuery.data,
    casesQuery.isLoading,
    casesQuery.isPending,
    casesQuery.isError,
  ]);

  useEffect(() => {
    if (scopeSearchTerm.trim().length > 2) {
      let scopeSearchTermToUse = scopeSearchTerm;
      if (
        scopeSearchTermToUse.startsWith('"') &&
        scopeSearchTermToUse.endsWith('"')
      ) {
        scopeSearchTermToUse = scopeSearchTermToUse.slice(1, -1);
      } else {
        scopeSearchTermToUse = scopeSearchTermToUse.trim();
      }
      const q = scopeSearchTermToUse.toLowerCase();
      setAvailableScopes(
        allScopes.filter((tag: string) => {
          const label = scopeLabelByTag[tag] ?? tag;
          return (
            tag.toLowerCase().includes(q) || label.toLowerCase().includes(q)
          );
        }),
      );
    } else {
      setAvailableScopes(allScopes);
    }
  }, [scopeSearchTerm, allScopes, scopeLabelByTag]);

  useEffect(() => {
    if (!isActive) {
      setPreviewPopoverOpen(-1);
      setPreviewPopoverUrl(null);
    }
  }, [isActive]);

  useEffect(() => {
    if (copiedSource !== -1) {
      const t = window.setTimeout(() => setCopiedSource(-1), 1500);
      return () => window.clearTimeout(t);
    }
  }, [copiedSource]);

  useEffect(() => {
    if (addedSource !== -1) {
      const t = window.setTimeout(() => setAddedSource(-1), 1500);
      return () => window.clearTimeout(t);
    }
  }, [addedSource]);

  useEffect(() => {
    if (previewPopoverOpen === -1 && previewPopoverUrl === null) return;
    if (isFullscreen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (
        target instanceof Element &&
        target.closest('[data-evidence-preview-dismiss-overlay]')
      ) {
        return;
      }
      if (
        previewPopoverRef.current &&
        !previewPopoverRef.current.contains(target)
      ) {
        event.stopPropagation();
        setPreviewPopoverOpen(-1);
        setPreviewPopoverUrl(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [previewPopoverOpen, previewPopoverUrl, isFullscreen]);

  const handleAddScopesAndFiles = useCallback(
    ({
      scopesToAdd,
      filesToAdd,
      excludedScopesToAdd,
      excludedFilesToAdd,
    }: {
      scopesToAdd: string[];
      filesToAdd: string[];
      excludedScopesToAdd: string[];
      excludedFilesToAdd: string[];
    }) => {
      setScopesAndFiles((prev) => {
        const newScopes = new Set([...(prev.scopes || []), ...scopesToAdd]);
        const newFiles = new Set([...(prev.files || []), ...filesToAdd]);
        const newExcludedScopes = new Set([
          ...(prev.excludedScopes || []),
          ...excludedScopesToAdd,
        ]);
        const newExcludedFiles = new Set([
          ...(prev.excludedFiles || []),
          ...excludedFilesToAdd,
        ]);
        for (const scope of scopesToAdd) newExcludedScopes.delete(scope);
        for (const file of filesToAdd) newExcludedFiles.delete(file);
        for (const scope of excludedScopesToAdd) newScopes.delete(scope);
        for (const file of excludedFilesToAdd) newFiles.delete(file);
        return {
          scopes: Array.from(newScopes),
          files: Array.from(newFiles),
          excludedScopes: Array.from(newExcludedScopes),
          excludedFiles: Array.from(newExcludedFiles),
          contentCategories: prev.contentCategories,
          excludedContentCategories: prev.excludedContentCategories,
          evidenceCategoryChips: prev.evidenceCategoryChips,
          excludedEvidenceCategoryScopes: prev.excludedEvidenceCategoryScopes,
          evidenceCategoryScopes: prev.evidenceCategoryScopes,
          evidenceAllFileTypes: prev.evidenceAllFileTypes,
        };
      });
      setSelectedScopes([]);
      setSelectedEvidence([]);
      setExcludedScopes([]);
      setExcludedEvidence([]);
    },
    [setScopesAndFiles],
  );

  const removeAppliedFileOrScope = useCallback(
    (item: string) => {
      setScopesAndFiles((prev) => ({
        ...prev,
        scopes: prev.scopes.filter((s) => s !== item),
        files: prev.files.filter((f) => f !== item),
        excludedScopes: prev.excludedScopes.filter((s) => s !== item),
        excludedFiles: prev.excludedFiles.filter((f) => f !== item),
      }));
    },
    [setScopesAndFiles],
  );

  const addToSelectedSources = useCallback(
    (sourceUrl: string) => {
      const isApplied = scopesAndFiles.files.includes(sourceUrl);
      const isPending = selectedEvidence.includes(sourceUrl);
      if (isApplied || isPending) {
        removeAppliedFileOrScope(sourceUrl);
        setSelectedEvidence((prev) => prev.filter((s) => s !== sourceUrl));
        setExcludedEvidence((prev) => prev.filter((s) => s !== sourceUrl));
      } else {
        handleAddScopesAndFiles({
          scopesToAdd: [],
          filesToAdd: [sourceUrl],
          excludedScopesToAdd: [],
          excludedFilesToAdd: [],
        });
      }
      setIsExcludeAllActive(false);
    },
    [
      scopesAndFiles.files,
      selectedEvidence,
      removeAppliedFileOrScope,
      handleAddScopesAndFiles,
    ],
  );

  const addToSelectedScopes = useCallback(
    (scopeIn: string) => {
      const isApplied = scopesAndFiles.scopes.includes(scopeIn);
      const isPending = selectedScopes.includes(scopeIn);
      if (isApplied || isPending) {
        removeAppliedFileOrScope(scopeIn);
        setSelectedScopes((prev) => prev.filter((s) => s !== scopeIn));
        setExcludedScopes((prev) => prev.filter((s) => s !== scopeIn));
      } else {
        handleAddScopesAndFiles({
          scopesToAdd: [scopeIn],
          filesToAdd: [],
          excludedScopesToAdd: [],
          excludedFilesToAdd: [],
        });
      }
      setIsExcludeAllActive(false);
    },
    [
      scopesAndFiles.scopes,
      selectedScopes,
      removeAppliedFileOrScope,
      handleAddScopesAndFiles,
    ],
  );

  const excludeFromSelectedSources = useCallback(
    (sourceUrl: string) => {
      const isExcluded = scopesAndFiles.excludedFiles.includes(sourceUrl);
      if (isExcluded) {
        removeAppliedFileOrScope(sourceUrl);
      } else {
        setScopesAndFiles((prev) => ({
          ...prev,
          files: prev.files.filter((f) => f !== sourceUrl),
          excludedFiles: Array.from(new Set([...prev.excludedFiles, sourceUrl])),
          contentCategories: prev.contentCategories,
          excludedContentCategories: prev.excludedContentCategories,
          evidenceCategoryChips: prev.evidenceCategoryChips,
          excludedEvidenceCategoryScopes: prev.excludedEvidenceCategoryScopes,
          evidenceCategoryScopes: prev.evidenceCategoryScopes,
          evidenceAllFileTypes: prev.evidenceAllFileTypes,
        }));
      }
      setSelectedEvidence((prev) => prev.filter((s) => s !== sourceUrl));
      setIsExcludeAllActive(false);
    },
    [scopesAndFiles.excludedFiles, removeAppliedFileOrScope, setScopesAndFiles],
  );

  const excludeFromSelectedScopes = useCallback(
    (scopeIn: string) => {
      const isExcluded = scopesAndFiles.excludedScopes.includes(scopeIn);
      if (isExcluded) {
        removeAppliedFileOrScope(scopeIn);
      } else {
        setScopesAndFiles((prev) => ({
          ...prev,
          scopes: prev.scopes.filter((s) => s !== scopeIn),
          excludedScopes: Array.from(new Set([...prev.excludedScopes, scopeIn])),
          contentCategories: prev.contentCategories,
          excludedContentCategories: prev.excludedContentCategories,
          evidenceCategoryChips: prev.evidenceCategoryChips,
          excludedEvidenceCategoryScopes: prev.excludedEvidenceCategoryScopes,
          evidenceCategoryScopes: prev.evidenceCategoryScopes,
          evidenceAllFileTypes: prev.evidenceAllFileTypes,
        }));
      }
      setSelectedScopes((prev) => prev.filter((s) => s !== scopeIn));
      setIsExcludeAllActive(false);
    },
    [scopesAndFiles.excludedScopes, removeAppliedFileOrScope, setScopesAndFiles],
  );

  const cycleFileScope = useCallback(
    (sourceUrl: string) => {
      const isPicked =
        scopesAndFiles.files.includes(sourceUrl) ||
        selectedEvidence.includes(sourceUrl);
      const isExcluded =
        scopesAndFiles.excludedFiles.includes(sourceUrl) ||
        excludedEvidence.includes(sourceUrl);

      if (!isPicked && !isExcluded) {
        setExcludedEvidence((prev) => prev.filter((s) => s !== sourceUrl));
        handleAddScopesAndFiles({
          scopesToAdd: [],
          filesToAdd: [sourceUrl],
          excludedScopesToAdd: [],
          excludedFilesToAdd: [],
        });
      } else if (isPicked) {
        setSelectedEvidence((prev) => prev.filter((s) => s !== sourceUrl));
        setScopesAndFiles((prev) => ({
          ...prev,
          files: prev.files.filter((f) => f !== sourceUrl),
          excludedFiles: Array.from(new Set([...prev.excludedFiles, sourceUrl])),
          contentCategories: prev.contentCategories,
          excludedContentCategories: prev.excludedContentCategories,
          evidenceCategoryChips: prev.evidenceCategoryChips,
          excludedEvidenceCategoryScopes: prev.excludedEvidenceCategoryScopes,
          evidenceCategoryScopes: prev.evidenceCategoryScopes,
          evidenceAllFileTypes: prev.evidenceAllFileTypes,
        }));
      } else {
        removeAppliedFileOrScope(sourceUrl);
        setExcludedEvidence((prev) => prev.filter((s) => s !== sourceUrl));
      }
      setIsExcludeAllActive(false);
      setIsIncludeAllActive(false);
    },
    [
      scopesAndFiles.files,
      scopesAndFiles.excludedFiles,
      selectedEvidence,
      excludedEvidence,
      handleAddScopesAndFiles,
      removeAppliedFileOrScope,
    ],
  );

  const cycleFolderScope = useCallback(
    (scopeIn: string) => {
      const isPicked =
        scopesAndFiles.scopes.includes(scopeIn) ||
        selectedScopes.includes(scopeIn);
      const isExcluded =
        scopesAndFiles.excludedScopes.includes(scopeIn) ||
        excludedScopes.includes(scopeIn);

      if (!isPicked && !isExcluded) {
        setExcludedScopes((prev) => prev.filter((s) => s !== scopeIn));
        handleAddScopesAndFiles({
          scopesToAdd: [scopeIn],
          filesToAdd: [],
          excludedScopesToAdd: [],
          excludedFilesToAdd: [],
        });
      } else if (isPicked) {
        setSelectedScopes((prev) => prev.filter((s) => s !== scopeIn));
        setScopesAndFiles((prev) => ({
          ...prev,
          scopes: prev.scopes.filter((s) => s !== scopeIn),
          excludedScopes: Array.from(new Set([...prev.excludedScopes, scopeIn])),
          contentCategories: prev.contentCategories,
          excludedContentCategories: prev.excludedContentCategories,
          evidenceCategoryChips: prev.evidenceCategoryChips,
          excludedEvidenceCategoryScopes: prev.excludedEvidenceCategoryScopes,
          evidenceCategoryScopes: prev.evidenceCategoryScopes,
          evidenceAllFileTypes: prev.evidenceAllFileTypes,
        }));
      } else {
        removeAppliedFileOrScope(scopeIn);
        setExcludedScopes((prev) => prev.filter((s) => s !== scopeIn));
      }
      setIsExcludeAllActive(false);
      setIsIncludeAllActive(false);
    },
    [
      scopesAndFiles.scopes,
      scopesAndFiles.excludedScopes,
      selectedScopes,
      excludedScopes,
      handleAddScopesAndFiles,
      removeAppliedFileOrScope,
    ],
  );

  const handleSetPreviewPopoverOpen = useCallback(
    (index: number, pageCount?: number) => {
      setPreviewPopoverOpen(index);
      setPreviewPageCount(pageCount);
      setShowObfuscatedPreview(true);
      // Index-based previews resolve their CM flag from the matching
      // ``availableEvidence`` entry, so drop any URL-based CM state.
      setPreviewPopoverCmType(null);
      setPreviewPopoverPreviewUrl(null);
      setPreviewPopoverFileInfo(null);
      if (index === -1) {
        setPreviewPopoverUrl(null);
        setPreviewPageCount(undefined);
      }
      const sourceItem = availableEvidence[index];
      if (sourceItem) {
        setPreviewPopoverUrl(null);
      }
      if (!linkedMediaItem) {
        // Clear both together: the app-level fullscreen viewer only mounts when
        // ``isFullscreen && fullscreenMediaItem`` are set, so nulling the item
        // without exiting fullscreen would hide the UI with no overlay/exit control.
        setFullscreenMediaItem?.(null);
        setIsFullscreen?.(false);
      }
    },
    [availableEvidence, linkedMediaItem, setFullscreenMediaItem, setIsFullscreen],
  );

  const handleSetPreviewPopoverOpenForUrl = useCallback(
    (
      url: string | null,
      pageCount?: number,
      cmType?: string | null,
      previewUrl?: string | null,
      fileInfo?: FileSummary | null,
    ) => {
      setPreviewPopoverUrl(url);
      setPreviewPageCount(pageCount);
      setPreviewPopoverCmType(cmType ?? null);
      setPreviewPopoverPreviewUrl(previewUrl ?? null);
      setPreviewPopoverFileInfo(fileInfo ?? null);
      if (url) {
        setPreviewPopoverOpen(-1);
        if (!linkedMediaItem) {
          // Clear both together so we never leave ``isFullscreen`` on without a
          // mounted fullscreen viewer (which would hide the UI with no exit control).
          setFullscreenMediaItem?.(null);
          setIsFullscreen?.(false);
        }
      } else {
        setPreviewPageCount(undefined);
      }
    },
    [linkedMediaItem, setFullscreenMediaItem, setIsFullscreen],
  );

  const handleOpenUfdrPreview = useCallback(
    (next: {
      url: string;
      fileStatusId?: string | null;
      label?: string;
      fileInfo?: FileSummary | null;
      path?: string | null;
      caseFolderName?: string | null;
    }) => {
      const searchable = next.path || next.fileInfo?.searchablePath;
      setUfdrPreviewContext({
        url: next.url,
        fileStatusId: next.fileStatusId ?? null,
        label: next.label,
        fileInfo: next.fileInfo ?? null,
        path: searchable ? toProvenanceLookupPath(searchable) : next.url,
        caseFolderName:
          next.caseFolderName ?? resolveCaseFolderName(casesQuery.data, caseId) ?? undefined,
      });
      setPreviewPopoverOpen(-1);
      setPreviewPopoverUrl(null);
      if (!linkedMediaItem) {
        setFullscreenMediaItem?.(null);
        setIsFullscreen?.(false);
      }
    },
    [linkedMediaItem, setFullscreenMediaItem, setIsFullscreen, casesQuery.data, caseId],
  );

  const handleCloseUfdrPreview = useCallback(() => {
    setUfdrPreviewContext(null);
    setPreviewPopoverOpen(-1);
    setPreviewPopoverUrl(null);
  }, []);

  const handleCopySourceForTimeline = useCallback(
    async (source: Source, index: number) => {
      try {
        const pathToCopy = extractPathAfterBulk(
          source.sourceFilePath || source.truncatedFilePath || '',
        ).truncatedFilePath;
        source.sourceContext = buildTimelineCopySourceContext(source);
        const sourceToCopy = { source, caseId };
        if (pathToCopy) {
          try {
            if (navigator.clipboard && window.isSecureContext) {
              await navigator.clipboard.writeText(pathToCopy);
            } else {
              const textArea = document.createElement('textarea');
              textArea.value = pathToCopy;
              textArea.style.position = 'fixed';
              textArea.style.left = '-999999px';
              document.body.appendChild(textArea);
              textArea.focus();
              textArea.select();
              document.execCommand('copy');
              textArea.remove();
            }
            sessionStorage.setItem('source', JSON.stringify(sourceToCopy));
            setCopiedSource(index);
          } catch {
            sessionStorage.setItem('source', JSON.stringify(sourceToCopy));
            setCopiedSource(index);
          }
        }
      } catch (error) {
        console.error('Failed to copy source path:', error);
      }
    },
    [caseId],
  );

  const showEvidenceRowsWhenFoldersOn =
    !showFolders ||
    allFileTypes ||
    activeFileTypeChipCount > 0 ||
    evidenceLockerCategoryScopeActive;
  const isFoldersOnlyMode = showFolders && !showEvidenceRowsWhenFoldersOn;

  // Same cache-collision caveat as the tree: a disabled query still reads the
  // untagged All Cases entry, so do not surface its count either.
  const evidenceTotal = caseScopeUnresolved
    ? 0
    : (filesQuery.data?.pages?.[0]?.total ?? availableEvidence?.length ?? 0);
  const hasResults =
    isFoldersOnlyMode ||
    (availableEvidence?.length > 0 && showEvidenceRowsWhenFoldersOn) ||
    (availableScopes?.length > 0 && (!allFileTypes || showFolders));

  const hasAnyPick =
    selectedEvidence.length > 0 ||
    selectedScopes.length > 0 ||
    excludedEvidence.length > 0 ||
    excludedScopes.length > 0;

  const pickedEvidenceSet = useMemo(
    () => new Set([...scopesAndFiles.files, ...selectedEvidence]),
    [scopesAndFiles.files, selectedEvidence],
  );
  const excludedEvidenceSet = useMemo(
    () => new Set([...scopesAndFiles.excludedFiles, ...excludedEvidence]),
    [scopesAndFiles.excludedFiles, excludedEvidence],
  );
  const pickedScopesSet = useMemo(
    () => new Set([...scopesAndFiles.scopes, ...selectedScopes]),
    [scopesAndFiles.scopes, selectedScopes],
  );
  const excludedScopesSet = useMemo(
    () => new Set([...scopesAndFiles.excludedScopes, ...excludedScopes]),
    [scopesAndFiles.excludedScopes, excludedScopes],
  );

  const handleClearPendingSelections = useCallback(() => {
    setSelectedEvidence([]);
    setSelectedScopes([]);
    setExcludedEvidence([]);
    setExcludedScopes([]);
    setIsExcludeAllActive(false);
    setIsIncludeAllActive(false);
  }, []);

  const handleSelectAll = useCallback(() => {
    setExcludedEvidence([]);
    setExcludedScopes([]);
    const visible = evidenceTreeRef.current?.getVisibleSelection();
    let fileUrls: string[] = [];
    let folderUrls: string[] = [];
    if (visible) {
      fileUrls = visible.fileUrls;
      folderUrls = visible.folderUrls;
    } else {
      if (showEvidenceRowsWhenFoldersOn) {
        fileUrls = availableEvidence?.map((source) => source.url) || [];
      }
      if (showFolders || (!allFileTypes && activeFileTypeChipCount < 1)) {
        folderUrls = availableScopes || [];
      }
    }
    handleAddScopesAndFiles({
      scopesToAdd: Array.from(new Set(folderUrls)),
      filesToAdd: showEvidenceRowsWhenFoldersOn
        ? Array.from(new Set(fileUrls))
        : [],
      excludedScopesToAdd: [],
      excludedFilesToAdd: [],
    });
    setIsIncludeAllActive(true);
    setIsExcludeAllActive(false);
  }, [
    showEvidenceRowsWhenFoldersOn,
    showFolders,
    allFileTypes,
    activeFileTypeChipCount,
    availableEvidence,
    availableScopes,
    handleAddScopesAndFiles,
  ]);

  const isSourceCardPopoverOpen =
    (previewPopoverOpen !== -1 || previewPopoverUrl !== null) &&
    isActive;

  const handlePurgeAdHocManualFile = useCallback(
    async (fileName: string) => {
      const trimmed = fileName.trim();
      if (!trimmed || !isAdHocEvidenceCase || !caseId) return;
      if (
        !window.confirm(`Delete "${trimmed}" from the ${AD_HOC_CASE_DISPLAY_NAME} Manual folder? This cannot be undone.`)
      ) {
        return;
      }

      if (!downloadCellId || !downloadAccessToken) {
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 4000,
          title: 'Delete Failed',
          description: 'No authenticated session. Please refresh and try again.',
          level: ToastLevel.ERROR,
        });
        return;
      }

      setPurgingManualFileName(trimmed);
      try {
        await purgeManualCaseFiles({
          cellId: downloadCellId,
          accessToken: downloadAccessToken,
          caseId,
          fileNames: [trimmed],
        });

        optimisticallyRemoveAdHocManualFile(queryClient, trimmed, caseId);

        setAvailableEvidence((prev) =>
          prev.filter((item) => !matchesAdHocManualFileName(item.url, trimmed, caseId)),
        );
        setScopesAndFiles((prev) => ({
          ...prev,
          files: prev.files.filter((url) => !matchesAdHocManualFileName(url, trimmed, caseId)),
          excludedFiles: prev.excludedFiles.filter(
            (url) => !matchesAdHocManualFileName(url, trimmed, caseId),
          ),
        }));
        setSelectedEvidence((prev) =>
          prev.filter((url) => !matchesAdHocManualFileName(url, trimmed, caseId)),
        );
        setExcludedEvidence((prev) =>
          prev.filter((url) => !matchesAdHocManualFileName(url, trimmed, caseId)),
        );

        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 4000,
          title: 'File Deleted',
          description: `${trimmed} was removed from ${AD_HOC_CASE_DISPLAY_NAME}.`,
          level: ToastLevel.SUCCESS,
        });

        await Promise.all([
          queryClient.refetchQueries({ queryKey: provenanceKeys.tree() }),
          queryClient.refetchQueries({ queryKey: provenanceKeys.files() }),
        ]);
      } catch (error) {
        const detail =
          error instanceof EdgeLambdaApiError && error.detail?.trim()
            ? error.detail.trim()
            : null;
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 4000,
          title: 'Delete Failed',
          description: detail ?? `Could not delete ${trimmed}.`,
          level: ToastLevel.ERROR,
        });
      } finally {
        setPurgingManualFileName(null);
      }
    },
    [
      isAdHocEvidenceCase,
      caseId,
      downloadCellId,
      downloadAccessToken,
      queryClient,
      setToast,
      setToastOpen,
      setScopesAndFiles,
    ],
  );

  return {
    caseId,
    featureFlags,
    contentCategoriesEnabled,
    isSmallScreen,
    isActive,
    isFullscreen,
    showFolders,
    allFileTypes,
    activeFileTypes,
    filesInputValue,
    setEvidenceInputValue,
    setScopeInputValue,
    selectedContentCategories,
    setScopesAndFiles,
    loadingEvidence,
    loadingScopes,
    filesSearchTerm,
    activeFileTypeChipCount,
    evidenceLockerCategoryScopeActive,
    showEvidenceRowsWhenFoldersOn,
    isFoldersOnlyMode,
    hasResults,
    hasAnyPick,
    evidenceFilesFilters,
    caseTag,
    caseScopeUnresolved,
    caseScopeMissing,
    evidenceTreeRef,
    previewPopoverRef,
    filterBarRowRef,
    pickedEvidenceSet,
    excludedEvidenceSet,
    pickedScopesSet,
    excludedScopesSet,
    availableEvidence,
    previewPopoverOpen,
    previewPopoverUrl,
    previewPageCount,
    hoveredIconKey,
    setHoveredIconKey,
    isSourceCardPopoverOpen,
    selectedEvidence,
    selectedScopes,
    excludedEvidence,
    excludedScopes,
    addToSelectedSources,
    addToSelectedScopes,
    excludeFromSelectedSources,
    excludeFromSelectedScopes,
    cycleFileScope,
    cycleFolderScope,
    handleAddScopesAndFiles,
    handleSetPreviewPopoverOpen,
    handleSetPreviewPopoverOpenForUrl,
    handleOpenUfdrPreview,
    handleCloseUfdrPreview,
    ufdrPreviewContext,
    handleCopySourceForTimeline,
    handleDownloadWithSession,
    handleClearPendingSelections,
    handleSelectAll,
    isIncludeAllActive,
    toggleFileType,
    toggleShowFolders,
    toggleAllFileTypes,
    addSourceToScope,
    addToTimeline,
    mediaSourceShare,
    copiedSource,
    addedSource,
    setPreviewPopoverOpen,
    setPreviewPopoverUrl,
    previewPopoverCmType,
    previewPopoverPreviewUrl,
    previewPopoverFileInfo,
    contentModerationAck,
    setContentModerationAck,
    showObfuscatedPreview,
    setShowObfuscatedPreview,
    isAdHocEvidenceCase,
    adHocAutoBrowseSubfolder,
    handlePurgeAdHocManualFile,
    purgingManualFileName,
    isAdHocManualTreeFile: (node: TreeNodeResponse) =>
      isAdHocEvidenceCase && isAdHocManualTreeFile(node, caseId, personalFilesCaseId),
  };
}
